<!-- 
    Diese Komponente stellt einen Zustand in einem Zustandsdiagramm dar.
 -->
<script lang="ts">
    import { getPositionInPane } from './context';
    import type { Node } from './state/node';
    import { grid_fitVector } from './grid';

    export let node: Node;
    export let fill: string;
    export let strokeWidth: string;
    export let enableDrag: boolean;

    // Wird aufgerufen, wenn die Node mit einem primären Pointer verschoben wird.
    export let onMove: (pos: {x: number, y: number}) => void;
    
    // wird aufgerufen, wenn die Node geklickt wird, ohne dass sie zwischen pointerDown und pointerUp verschoben wird.
    // Der Click-Event des Browsers wird auch dann aufgerufen, wenn die Node zwischen pointerDown und pointerUp verschoben wird.
    // Deshalb wir der Click-Event des Browsers ignoriert.
    export let onClick = () => {};

    const positionInPane = getPositionInPane();

    // Falls mehrere PointerTypes (mouse, pen, touch) verwendet werden, kann es mehrere primary Pointer geben 
    // (einen primary Pointer für jeden PointerType).
    // Ein Capture wird immer nur für einen primären Pointer gemacht.
    // Falls ein primärer Pointer gedrückt wird (down), während ein anderer primärer Pointer nocht gedrückt (active) ist,
    // gibt es zwei gecapturete Pointer. In diesem Fall soll nur der zuletzt gedrückte Pointer die Node verschieben oder einen Click auslösen.
    // Deshalb wird gespeichert, welcher Pointer zuletzt gecaptured wurde.
    let lastCapturedPointerId: number | undefined = undefined;
    let pointerHasBeenMoved = false;
    let dragOffset = {x:0, y:0};

    $: textLines = node.name.split(/\r?\n|\r|\n/g);
    $: textHeightInEm = textLines.length + (textLines.length -1) * 0.2;
    $: translateTextY = -textHeightInEm / 2;

    function handleStateClicked(event) {
        // console.log('BaseNode - clicked');
        event.stopPropagation();
    }

    function handleStatePointerDown(event: PointerEvent) {
        // console.log('BaseNode - pointerDown');

        if (!event.isPrimary) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        
        nodeElement.setPointerCapture(event.pointerId);
        lastCapturedPointerId = event.pointerId;

        pointerHasBeenMoved = false;
    
        const pointerPosition = positionInPane(event.clientX, event.clientY);

        dragOffset = {
            x: pointerPosition.x - node.posx,
            y: pointerPosition.y - node.posy,
        }
    }

    function handlePointerMove(event: PointerEvent) {
        // console.log('BaseNode - pointerMove');

        if (!event.isPrimary) {
            return;
        }

        if (lastCapturedPointerId !== event.pointerId) {
            // console.log('BaseNode - pointerMove - Das ist nicht der Pointer, der zuletzt gecaptured wurde.');
            return;
        }

        pointerHasBeenMoved = true;

        if (enableDrag) {
            const pointerPosition = positionInPane(event.clientX, event.clientY);
            const posx = pointerPosition.x - dragOffset.x;
            const posy = pointerPosition.y - dragOffset.y;
            
            onMove(grid_fitVector({x: posx, y: posy}));
        }
    }

    function handlePointerUp(event: PointerEvent) {
        // console.log('BaseNode - pointerUp');

        if (!event.isPrimary) {
            return;
        }

        if (lastCapturedPointerId !== event.pointerId) {
            // console.log('BaseNode - pointerUp - Das ist nicht der Pointer, der zuletzt gecaptured wurde.');
            return;
        }

        if (!pointerHasBeenMoved) {
            onClick();
        }

        pointerHasBeenMoved = false;
    }

    function handleLostPointerCapture(event: PointerEvent) {
        // console.log('BaseNode - lostPointerCapture');
        if (lastCapturedPointerId === event.pointerId) {
            lastCapturedPointerId = undefined;
        }
    }

    let nodeElement;
</script>

<g transform="translate({node.posx} {node.posy})">
    {#if node.type === 'NodeCircle'}
        {#if node.isStart}
            <line 
                x1="-60"
                y1="0"
                x2="-20"
                y2="0"
                stroke="black"
                />
                <!-- marker-end="url(#markerArrow)" -->
                {/if}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <circle
            bind:this={nodeElement}
            cx={0}
            cy={0}
            r=20 
            stroke="black"
            fill={fill}
            on:click={handleStateClicked}
            on:pointerdown={handleStatePointerDown}
            on:pointermove={handlePointerMove}
            on:pointerup={handlePointerUp}
            on:lostpointercapture={handleLostPointerCapture}
            >
        </circle>
        {#if node.isFinal}
            <circle 
                cx={0}
                cy={0}
                r=16
                fill={fill}
                stroke="black"
                pointer-events="none">
            </circle>
        {/if}
    {/if}
    
    {#if node.type === 'NodeRect'}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <rect
            bind:this={nodeElement}
            x={-node.width/2}
            y={-node.height/2}
            width={node.width}
            height={node.height}
            stroke="black"
            stroke-width={strokeWidth}
            fill={fill}
            rx="5"
            on:click={handleStateClicked}
            on:pointerdown={handleStatePointerDown}
            on:pointermove={handlePointerMove}
            on:pointerup={handlePointerUp}
            on:lostpointercapture={handleLostPointerCapture}
        />
        {#if node.isStart}
            <line 
                x1={-node.width/2 - 40}
                y1="0"
                x2={-node.width/2}
                y2="0"
                stroke="black"
                marker-end="url(#markerArrow)"
            />
        {/if}
    {/if}
    
    <!-- <circle cx="0" cy="0" r ="1" fill="black" /> -->
    <!-- dominant-baseline="hanging" wird von Safari scheinbar nicht unterstützt (Stand März 2024) -->
    <!-- 
        Achtung: Der Wert für dy bei dem ersten tspan ist geschätzt. Der genaue Wert hängt vom descent der Schriftart ab. 
        Je nach Schriftart, die der svg-Renderer verwendet, kann der Text deshalb in y-Richtung leicht unterschiedlich positioniert sein.
    -->
    <text 
        y="{translateTextY}em"
        text-anchor="middle"
        font-family="sans-serif"
    >
        {#each textLines as line, lineNr}
            {#if lineNr === 0}
                <tspan x="{0}" dy="0.85em">{line}</tspan>
            {:else}
                <tspan x="{0}" dy="1.2em">{line}</tspan>
            {/if}
        {/each}
    </text>
</g>

<style>
    text {
        pointer-events: none;
        user-select: none;
    }
</style>